import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function FounderSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.founder-reveal');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 200);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-6xl mx-auto">
          {/* Image Side */}
          <div className="founder-reveal opacity-0 translate-y-6 transition-all duration-1000 lg:w-1/2"
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <div className="relative">
              {/* Decorative frame */}
              <div className="absolute -inset-3 md:-inset-5 border-2 border-primary-200/40 rounded-3xl" />
              <div className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 w-24 h-24 md:w-32 md:h-32 bg-primary-100/40 rounded-full blur-2xl" />
              <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 w-20 h-20 md:w-28 md:h-28 bg-secondary-100/40 rounded-full blur-2xl" />

              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                {/* REAL USER PHOTO — salon interior with founder */}
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_8a9ab67bf10bd6660b0441c9dc9a6848_95722c27.jpg"
                  alt="Mme Ahui Brigitte, fondatrice de KD'S BEAUTY dans son salon à Angré Château"
                  className="w-full h-[400px] md:h-[500px] object-cover object-top"
                />
                {/* Overlay badge */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/70 to-transparent p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center border border-background-200/50">
                      <img
                        src="https://static.readdy.ai/image/66108e401cf688a02511c33e687ef000/027bb56ccb9ee3af4b09b48629b253d4.png"
                        alt="Logo KD'S BEAUTY"
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-background-50">Mme Ahui Brigitte</p>
                      <p className="text-xs text-background-50/70">Fondatrice & Esthéticienne</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text Side */}
          <div className="founder-reveal opacity-0 translate-y-6 transition-all duration-1000 lg:w-1/2"
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', transitionDelay: '200ms' }}
          >
            <span className="inline-block px-4 py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4 tracking-wider uppercase">
              La Fondatrice
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-800 mb-4 leading-tight">
              Mme Ahui Brigitte
            </h2>
            <p className="text-primary-600 text-sm tracking-wider uppercase mb-6 font-medium">
              Esthéticienne diplômée — 5 ans d'expertise
            </p>
            <p className="text-foreground-600 leading-relaxed mb-4 text-sm md:text-base">
              KD'S BEAUTY est né d'une passion profonde pour la beauté africaine et le bien-être. Fondé en 2025 à Angré Château, notre spa est le fruit d'un rêve : créer un refuge où chaque femme peut se reconnecter à elle-même.
            </p>
            <p className="text-foreground-600 leading-relaxed mb-6 text-sm md:text-base">
              Chaque détail a été pensé pour votre confort : des lumières tamisées aux senteurs apaisantes, en passant par nos cabines spacieuses et notre espace détente. Notre équipe passionnée met tout son savoir-faire à votre service.
            </p>

            {/* Signature quote */}
            <blockquote className="relative pl-5 py-3 mb-6">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-400 rounded-full" />
              <p className="text-foreground-700 italic text-sm md:text-base leading-relaxed">
                "La beauté n'est pas qu'une question d'apparence. C'est un état d'esprit, une confiance en soi qui rayonne."
              </p>
              <p className="text-xs text-primary-500 mt-2 font-medium">— Mme Ahui Brigitte</p>
            </blockquote>

            <Link
              to="/univers"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-300"
            >
              Découvrir notre histoire
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-xs" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}